import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Text, Select as UISelect } from '@bufferapp/ui';
import { isInThePast } from '~/server/formatters';
import { selectHasTwentyFourHourTimeFormat } from '~/user/selectors';
import getCtaProperties from '~/analytics-middleware/utils/CtaStrings';
import { SEGMENT_NAMES } from '~/constants';
import { actions as previewActions } from '~/story-preview';
import { actions as analyticsActions } from '~/analytics-middleware/actions';
import DateTimeSlotPickerWrapper from '../DateTimeSlotPickerWrapper';
import { getReadableDateFormat, getMomentTime } from '../../utils/AddStory';
import { storyGroupPropTypes } from '../../utils/commonPropTypes';
import isObjectEmpty from '../../../utils/isObjectEmpty';
import { actions } from '../../reducer';
import { getCounts } from '../../utils/Tracking';
import {
  FooterBar,
  ButtonStyle,
  EditStoryStyle,
  EditTextStyle,
  EditDateStyle,
  StyledEditButton,
  StyledSelectButton,
} from './style';

class Select extends UISelect {
  handleSelectOption = (option, event) => {
    this.props.onSelectClick(option, event);
    this.setState({
      isOpen: false,
    });
  };
}

const getInitialDateTime = ({
  editMode,
  sentPost,
  scheduledAt,
  emptySlotData,
  timezone,
}) => {
  if ((editMode && !sentPost) || !isObjectEmpty(emptySlotData)) {
    const timestamp = editMode ? scheduledAt : emptySlotData.scheduledAt;
    return getMomentTime({ scheduledAt: timestamp, timezone });
  }

  return null;
};

const getIsScheduleDisabled = ({
  editMode,
  storiesLength,
  uploadsCompleted,
  isScheduleLoading,
  isScheduledAtPastDue,
}) =>
  storiesLength < 1 ||
  !uploadsCompleted ||
  isScheduleLoading ||
  (!editMode && isScheduledAtPastDue);

const AddStoryFooter = ({ editMode, storyGroup, isPastDue, type }) => {
  const dispatch = useDispatch();
  const sentPost = type === 'pastReminders';
  const uses24hTime = useSelector(selectHasTwentyFourHourTimeFormat);
  const translations = useSelector(
    (state) => state.i18n.translations['story-group-composer']
  );
  const storiesState = useSelector((state) => state.stories);
  const selectedProfile = useSelector(
    (state) => state.profileSidebar.selectedProfile
  );
  const isScheduleLoading = useSelector(
    (state) => state.storyGroupComposer.isScheduleLoading
  );

  const { id, serviceId, timezone } = selectedProfile;
  const { emptySlotData = {} } = storiesState;

  const [scheduledAt, setScheduledAt] = useState(
    storyGroup && !sentPost ? storyGroup.scheduledAt : null
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [forceDatePickerSubmit, setForceDatePickerSubmit] = useState(false);

  /* this covers the case if a story group has a share failure and a user edits it
   without updating the scheduled_at. Now the schedule button will be disabled until
   date is updated. */
  const isScheduledAtPastDue = isPastDue && isInThePast(scheduledAt);

  const storiesLength = storyGroup.stories.length;
  const uploadsCompleted =
    storyGroup.stories.filter((card) => card.processing || card.uploading)
      .length === 0;

  const isScheduleDisabled = getIsScheduleDisabled({
    storiesLength,
    uploadsCompleted,
    isScheduleLoading,
    isScheduledAtPastDue,
    editMode,
  });
  const isPreviewDisabled = storiesLength < 1 || !uploadsCompleted;

  const { stories, storyGroupId } = storyGroup;

  const onCreateStoryGroup = (timestamp, shareNow = false) => {
    dispatch(actions.setScheduleLoading(true));
    dispatch(actions.handleSaveStoryGroup(timestamp, shareNow));
  };

  const onUpdateStoryGroup = ({ timestamp, shareNow = false }) => {
    dispatch(actions.setScheduleLoading(true));
    dispatch(
      actions.handleUpdateStoryGroup({
        scheduledAt: timestamp,
        stories,
        storyGroupId,
        shareNow,
      })
    );
  };

  const onDateTimeSlotPickerSubmit = (timestamp) => {
    setShowDatePicker(false);
    if (editMode) {
      if (forceDatePickerSubmit) {
        setForceDatePickerSubmit(false);
        if (sentPost) {
          onCreateStoryGroup(timestamp);
        } else {
          onUpdateStoryGroup({
            timestamp,
          });
        }
      } else {
        setScheduledAt(timestamp);
      }
    } else {
      onCreateStoryGroup(timestamp);
    }
  };

  const onScheduleClick = () => {
    if (
      !editMode ||
      (editMode && isScheduledAtPastDue) ||
      (editMode && sentPost)
    ) {
      setForceDatePickerSubmit(true);
      setShowDatePicker(true);
    } else {
      onUpdateStoryGroup({
        timestamp: scheduledAt,
      });
    }
  };

  const onShareNowClick = () => {
    if (editMode) {
      if (sentPost) {
        onCreateStoryGroup(null, true);
      } else {
        onUpdateStoryGroup({
          timestamp: scheduledAt,
          shareNow: true,
        });
      }
    } else {
      onCreateStoryGroup(null, true);
    }
  };

  const onFooterClick = () => {
    if (showDatePicker) {
      setShowDatePicker(false);
    }
  };

  const onPreviewClick = ({ profileId }) => {
    const ctaProperties = getCtaProperties(
      SEGMENT_NAMES.STORIES_PREVIEW_COMPOSER
    );
    const counts = getCounts(stories);

    const metadata = {
      storyGroupId,
      channel: 'instagram',
      channelId: profileId,
      channelServiceId: serviceId,
      scheduledAt: scheduledAt ? JSON.stringify(scheduledAt) : undefined,
      ...counts,
      ...ctaProperties,
    };
    dispatch(
      previewActions.handlePreviewClick({
        stories,
        profileId,
        id,
        scheduledAt,
      })
    );
    dispatch(actions.handlePreviewClick());
    dispatch(analyticsActions.trackEvent('Story Group Previewed', metadata));
  };

  return (
    <>
      <FooterBar onClick={onFooterClick}>
        {editMode && !sentPost && (
          <EditStoryStyle>
            <EditTextStyle>
              <Text type="p">Story Schedule:</Text>
            </EditTextStyle>
            <EditDateStyle>
              <Text>
                {getReadableDateFormat({
                  uses24hTime,
                  scheduledAt,
                  timezone,
                })}
              </Text>
            </EditDateStyle>
            <StyledEditButton
              label="Edit"
              type="secondary"
              size="small"
              onClick={() => setShowDatePicker(true)}
            />
          </EditStoryStyle>
        )}
        <ButtonStyle>
          <Button
            type="secondary"
            label={translations.previewButton}
            disabled={isPreviewDisabled}
            onClick={() =>
              onPreviewClick({
                profileId: id,
              })
            }
          />
        </ButtonStyle>
        <StyledSelectButton
          onClick={onScheduleClick}
          isSplit
          type="primary"
          disabled={isScheduleDisabled}
          label={
            isScheduleLoading
              ? translations.scheduleLoadingButton
              : translations.scheduleButton
          }
        >
          <Select
            onSelectClick={(selectedItem) => {
              if (typeof selectedItem.selectedItemClick !== 'undefined') {
                selectedItem.selectedItemClick();
              }
              return false;
            }}
            items={[
              {
                title: translations.shareNowButton,
                selectedItemClick: onShareNowClick,
              },
              {
                title: translations.scheduleButton,
                selectedItemClick: onScheduleClick,
              },
            ]}
            type="primary"
            isSplit
            xPosition="right"
            hideSearch
          />
        </StyledSelectButton>
      </FooterBar>
      {showDatePicker && (
        <DateTimeSlotPickerWrapper
          timezone={timezone}
          editMode={editMode}
          onDateTimeSlotPickerSubmit={(timestamp) =>
            onDateTimeSlotPickerSubmit(timestamp)
          }
          initialDateTime={getInitialDateTime({
            editMode,
            sentPost,
            scheduledAt,
            emptySlotData,
            timezone,
          })}
        />
      )}
    </>
  );
};

AddStoryFooter.propTypes = {
  ...DateTimeSlotPickerWrapper.propTypes,
  storyGroup: storyGroupPropTypes, // eslint-disable-line react/require-default-props,
  isPastDue: PropTypes.bool,
  emptySlotData: PropTypes.shape({
    scheduledAt: PropTypes.string,
  }),
};

AddStoryFooter.defaultProps = {
  storyGroup: {},
  isPastDue: false,
  emptySlotData: {},
};

export default AddStoryFooter;
